import { useEffect } from 'react';
import { ensureFreshKulaToken } from '../api/kulaToken';

// How often to re-check the token's remaining life. The check is cheap (decode a
// JWT); a refresh only actually fires once the token enters the pre-expiry
// window, so a 1-min cadence keeps it fresh without churn.
const CHECK_INTERVAL_MS = 60 * 1000;

// useKulaTokenSync keeps the Kula token fresh while the AgentForge chat is
// mounted, so every request the app sends (which AgentForge stores + reuses)
// carries a full-life token. Self-contained (not woven into the send/session
// flow): it just refreshes localStorage; the fresh token then rides the next
// request. Three triggers, because none alone is enough:
//   • on mount        — refresh immediately if already near expiry.
//   • interval (1 min)— steady-state monitor; refreshes inside the window.
//   • tab visibility  — background tabs throttle/pause timers and laptop sleep
//                       freezes them, so a long-idle tab re-checks on focus.
// The reactive `kula.token_expired` WS event is handled in useAgentForgeStream,
// which also calls ensureFreshKulaToken.
export const useKulaTokenSync = (): void => {
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (!cancelled) void ensureFreshKulaToken();
    };

    tick();
    const intervalId = window.setInterval(tick, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
};
